/**
 * Proposals/DeficienciesStep/Page — flag-ON Deficiencies wizard island (IP-15551 task-06).
 *
 * Mounted by react_component 'Proposals/DeficienciesStep/Page' inside the
 * deficiencies ERB form. Wizard chrome (Back/Next) stays in the ERB form.
 *
 * Architecture:
 * - One QueryClientProvider so MassActions invalidations propagate across
 *   the linked list and both Brushaway overlays without a separate client.
 * - Do NOT nest an extra QueryClientProvider inside the detail Brushaways
 *   (unlike EmbeddedDeficiencyDetailBrushaway which adds its own client).
 *
 * Photo carousel opens from the Photos column (candidate table) and grid thumbnails.
 *
 * Hide-from-customer checkbox was removed (IP-15551 QA, task-17). The unused
 * `proposals.hide_deficiencies_from_customer` column may remain for later
 * product work. Do not reintroduce the control without a defined behavior.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, showErrorToast } from '@ui';
import { SectionHeader } from '@ui/molecules/Header/SectionHeader';
import { translate } from '@utils/i18n';
import { Brushaway } from '@ui/molecules/Brushaway';
import EmbeddedDeficiencyDetail from '@features/Deficiencies/DeficiencyShow/EmbeddedDeficiencyDetail';
import { DeficienciesList } from './DeficienciesList';
import { AddOpenDeficienciesBrushaway } from './AddOpenDeficienciesBrushaway';
import { mapApiRowToViewModel } from './mapper';
import { proposalDeficiencyKeys } from './MassActions';
import { fetchLinkedDeficiencies, unlinkDeficiency, toggleDeficiencyPhotos } from './services/DeficienciesStepService';
import { DEFAULT_DEFICIENCY_FILTERS, isDefaultDeficiencyFilters } from './types';
import './PageStyles.scss';
const NS = 'features.proposals.deficiencies_step';
const t = (key) => translate(key, { namespace: NS });
// ---------------------------------------------------------------------------
// Inner — consumes the page-level QueryClient
// ---------------------------------------------------------------------------
function DeficienciesStepInner({ proposalId, assetsForSelect = [], severityOptions = [] }) {
    var _a;
    const queryClient = useQueryClient();
    // Linked list state
    const [linkedSelected, setLinkedSelected] = useState([]);
    const [linkedFilters, setLinkedFilters] = useState(DEFAULT_DEFICIENCY_FILTERS);
    const [linkedGroupBy, setLinkedGroupBy] = useState(null);
    // Add brushaway state
    const [isAddOpen, setIsAddOpen] = useState(false);
    // Linked compact detail state
    const [linkedDetailId, setLinkedDetailId] = useState(null);
    const [linkedDetailRow, setLinkedDetailRow] = useState(null);
    // ---------------------------------------------------------------------------
    // Linked query
    // ---------------------------------------------------------------------------
    const { data: rawLinked = [], isLoading, isError } = useQuery([...proposalDeficiencyKeys.linked(proposalId), linkedFilters], () => fetchLinkedDeficiencies(proposalId, linkedFilters));
    const linkedRows = useMemo(() => rawLinked.map(mapApiRowToViewModel), [rawLinked]);
    // ---------------------------------------------------------------------------
    // Selection
    // ---------------------------------------------------------------------------
    const handleLinkedSelectionChange = useCallback((id, checked) => {
        setLinkedSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
    }, []);
    const handleLinkedSelectAll = useCallback((selectAll) => {
        setLinkedSelected(selectAll ? linkedRows.map((r) => r.id) : []);
    }, [linkedRows]);
    // ---------------------------------------------------------------------------
    // Unlink single row
    // ---------------------------------------------------------------------------
    const unlinkMutation = useMutation((id) => unlinkDeficiency(proposalId, id), {
        onSuccess: () => {
            void queryClient.invalidateQueries(proposalDeficiencyKeys.linked(proposalId));
            void queryClient.invalidateQueries(proposalDeficiencyKeys.candidates(proposalId));
        },
        onError: () => {
            showErrorToast(t('error_unlink'));
        }
    });
    const handleRemove = useCallback((id) => {
        unlinkMutation.mutate(id);
    }, [unlinkMutation]);
    // ---------------------------------------------------------------------------
    // Toggle include photos for a single row
    // ---------------------------------------------------------------------------
    const togglePhotosMutation = useMutation(({ id, include }) => toggleDeficiencyPhotos(proposalId, id, include), {
        onSuccess: () => {
            void queryClient.invalidateQueries(proposalDeficiencyKeys.linked(proposalId));
        },
        onError: () => {
            showErrorToast(t('error_toggle_photos'));
        }
    });
    const handleToggleIncludePhotos = useCallback((id, include) => {
        togglePhotosMutation.mutate({ id, include });
    }, [togglePhotosMutation]);
    // ---------------------------------------------------------------------------
    // Linked compact detail
    // ---------------------------------------------------------------------------
    const handleOpenLinkedDetail = useCallback((id) => {
        var _a;
        const row = (_a = linkedRows.find((r) => r.id === id)) !== null && _a !== void 0 ? _a : null;
        setLinkedDetailRow(row);
        setLinkedDetailId(id);
    }, [linkedRows]);
    const handleCloseLinkedDetail = useCallback(() => {
        setLinkedDetailId(null);
        setLinkedDetailRow(null);
    }, []);
    const handleLinkedChange = useCallback((linked) => {
        if (!linked) {
            // Removed from proposal via compact footer → close detail
            handleCloseLinkedDetail();
        }
        void queryClient.invalidateQueries(proposalDeficiencyKeys.linked(proposalId));
        void queryClient.invalidateQueries(proposalDeficiencyKeys.candidates(proposalId));
    }, [queryClient, proposalId, handleCloseLinkedDetail]);
    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    const isEmpty = !isLoading && linkedRows.length === 0 && isDefaultDeficiencyFilters(linkedFilters);
    const addOpenDeficienciesButton = (React.createElement(Button, { type: "button", onClick: () => setIsAddOpen(true) },
        React.createElement("i", { className: "fa-light fa-circle-plus", "aria-hidden": "true" }),
        t('add_open_deficiencies')));
    return (React.createElement("div", { className: "proposal-deficiencies-step" },
        React.createElement(SectionHeader, { title: t('section_title'), actions: addOpenDeficienciesButton }),
        React.createElement("div", { className: "proposal-deficiencies-step__body" }, isEmpty ? (React.createElement("div", { className: "proposal-deficiencies-step__empty", "data-testid": "empty-linked-state" },
            React.createElement("p", { className: "proposal-deficiencies-step__empty-text" }, t('empty_linked')))) : (React.createElement(DeficienciesList, { mode: "linked", rows: linkedRows, isLoading: isLoading, isError: isError, proposalId: proposalId, selectedItems: linkedSelected, onSelectionChange: handleLinkedSelectionChange, onSelectAll: handleLinkedSelectAll, filters: linkedFilters, onFiltersChange: setLinkedFilters, groupBy: linkedGroupBy, onGroupByChange: setLinkedGroupBy, onOpenDetail: handleOpenLinkedDetail, onToggleIncludePhotos: handleToggleIncludePhotos, onRemove: handleRemove, assetsForSelect: assetsForSelect, severityOptions: severityOptions }))),
        React.createElement(AddOpenDeficienciesBrushaway, { proposalId: proposalId, isOpen: isAddOpen, onClose: () => setIsAddOpen(false), assetsForSelect: assetsForSelect, severityOptions: severityOptions }),
        React.createElement(Brushaway, { controlledOpen: linkedDetailId !== null, onClose: handleCloseLinkedDetail }, linkedDetailId !== null && (React.createElement(EmbeddedDeficiencyDetail, { deficiencyId: linkedDetailId, onClose: handleCloseLinkedDetail, variant: "proposalCompact", proposalId: proposalId, isLinked: true, onLinkedChange: handleLinkedChange, includedPhotoIds: (_a = linkedDetailRow === null || linkedDetailRow === void 0 ? void 0 : linkedDetailRow.includedPhotoIds) !== null && _a !== void 0 ? _a : [] })))));
}
// ---------------------------------------------------------------------------
// Default export — page root with its own QueryClient
// ---------------------------------------------------------------------------
export default function DeficienciesStepPage({ proposalId, assetsForSelect, severityOptions }) {
    const queryClient = useMemo(() => new QueryClient({
        defaultOptions: { queries: { staleTime: 30 * 1000, retry: 1 } }
    }), []);
    return (React.createElement(QueryClientProvider, { client: queryClient },
        React.createElement(DeficienciesStepInner, { proposalId: proposalId, assetsForSelect: assetsForSelect, severityOptions: severityOptions })));
}
